'use client';

import { BrainIcon, ChevronsLeft, ImagesIcon, LayersIcon, TagsIcon, WebhookIcon } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { memo, useEffect, useState } from 'react';

import { STypography } from 'components/ui';

import { LOCAL_STORAGE } from 'lib/constants/local-storage';

import { SidebarLink } from './sidebar-link';
import SidebarRobot from './sidebar-robot/sidebar-robot';
import { SLogoContainer, SNavigationLink, SNavigationList, SSidebar, SToggleItem } from './styled';

function RoboticsPlatformReplySmallIcon() {
  return (
    <svg
      className="logo-small"
      id="T_D_Reply"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 163.36 163.68"
    >
      <defs>
        <style>{'.cls-1{fill:hsl(var(--primary))}'}</style>
      </defs>
      <title>RoboticsPlatform-reply-small</title>
      <g id="Logo">
        <g id="RUNNING_MAN">
          <path
            className="cls-1"
            d="M207,59.89a16.4,16.4,0,1,0,16.39,16.42A16.39,16.39,0,0,0,207,59.89Z"
            transform="translate(-60.05 -59.89)"
          />
          <path
            className="cls-1"
            d="M171.08,128.86l20.48-27a4.19,4.19,0,0,0,.22-4.73l-8.7-14a4.17,4.17,0,0,0-3.57-2L142,81.52H121.4a1.71,1.71,0,0,0-1.08,3l5.77,4.65a6.2,6.2,0,0,0,2.8,1.29l35.74,6.74-44.21,47.52L62.22,153a2.52,2.52,0,0,0-1.49,4.21l5.69,6.1a5.81,5.81,0,0,0,4.35,1.85l56.18-.92a11.66,11.66,0,0,0,5.89-1.71l13.47-8.19,28.41,22.55a5.53,5.53,0,0,1,2,3.59l5.41,39.81a3.78,3.78,0,0,0,3.75,3.28h2.89a3.79,3.79,0,0,0,3.79-3.77l.17-44.19a10.67,10.67,0,0,0-2.08-6.33L165.9,135.87Z"
            transform="translate(-60.05 -59.89)"
          />
        </g>
      </g>
    </svg>
  );
}

function RoboticsPlatformReplyIcon() {
  return (
    <svg
      className="logo-big"
      id="Layer_1"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 613.97 191.71"
    >
      <defs>
        <style>
          {'.cls-1{fill:hsl(var(--primary));}.cls-2{fill:#fff;}.light .cls-2{fill:#151F27;}'}
        </style>
      </defs>
      <title>logo</title>
      <g id="Logo">
        <g id="RUNNING_MAN">
          <path
            className="cls-1"
            d="M192.93,45.88a16.4,16.4,0,1,0,16.39,16.41A16.38,16.38,0,0,0,192.93,45.88Z"
            transform="translate(-45.96 -45.88)"
          />
          <path
            className="cls-1"
            d="M157,114.84l20.48-26.95a4.19,4.19,0,0,0,.22-4.73l-8.7-14a4.18,4.18,0,0,0-3.57-2l-37.53.27h0l-20.58,0a1.71,1.71,0,0,0-1.07,3L112,75.18a6.42,6.42,0,0,0,2.8,1.29l35.74,6.73-44.21,47.52L48.13,139a2.52,2.52,0,0,0-1.49,4.21l5.69,6.09a5.79,5.79,0,0,0,4.35,1.85l56.18-.92a11.71,11.71,0,0,0,5.9-1.7l13.46-8.2,28.41,22.55a5.55,5.55,0,0,1,2,3.59l5.41,39.82a3.78,3.78,0,0,0,3.75,3.27h2.89a3.78,3.78,0,0,0,3.79-3.77l.17-44.19a10.53,10.53,0,0,0-2.08-6.33l-24.79-33.41Z"
            transform="translate(-45.96 -45.88)"
          />
        </g>
        <path
          className="cls-2"
          d="M298.44,171.2l-19.37-35H263.69v35h-21V72.58h46.13c20.56,0,33.27,13.46,33.27,31.79,0,17.3-11.09,26.76-21.73,29.28l22.32,37.55Zm2.07-67c0-8.13-6.36-13.16-14.64-13.16H263.69v26.62h22.18C294.15,117.68,300.51,112.65,300.51,104.22Z"
          transform="translate(-45.96 -45.88)"
        />
        <path
          className="cls-2"
          d="M337.76,171.2V72.58h69.79V91.06H358.76v20.7h47.75v18.49H358.76v22.47h48.79V171.2Z"
          transform="translate(-45.96 -45.88)"
        />
        <path
          className="cls-2"
          d="M424,171.2V72.58h46.13c21.44,0,33.12,14.49,33.12,31.79,0,17.15-11.83,31.64-33.12,31.64H445V171.2Zm57.81-66.83c0-8.28-6.36-13.31-14.64-13.31H445v26.47h22.18C475.41,117.53,481.77,112.5,481.77,104.37Z"
          transform="translate(-45.96 -45.88)"
        />
        <path
          className="cls-2"
          d="M515.48,171.2V72.58h21.14v80.14h41.7V171.2Z"
          transform="translate(-45.96 -45.88)"
        />
        <path
          className="cls-2"
          d="M601.23,171.2V130.84L563.38,72.58h24l24.4,39.48,24.4-39.48h23.8l-37.7,58.26V171.2Z"
          transform="translate(-45.96 -45.88)"
        />
        <path
          className="cls-1"
          d="M266.16,236.85l-9.05-15.34H249.9v15.34h-7.21V195.76h18.05c8.13,0,13.49,5.24,13.49,12.88,0,7.27-4.8,11.27-9.79,12.07l10.1,16.14Zm.68-28.28c0-3.94-3-6.46-7-6.46H249.9v13.06h9.92C263.88,215.17,266.84,212.58,266.84,208.57Z"
          transform="translate(-45.96 -45.88)"
        />
        <path
          className="cls-1"
          d="M279.63,216.34c0-12.26,8.69-21.26,21-21.26s21,9,21,21.26-8.74,21.25-21,21.25S279.63,228.59,279.63,216.34Zm34.62,0c0-8.51-5.36-14.85-13.61-14.85S287,207.83,287,216.34s5.29,14.84,13.61,14.84S314.25,224.78,314.25,216.34Z"
          transform="translate(-45.96 -45.88)"
        />
        <path
          className="cls-1"
          d="M328.55,236.85V195.76h20.2c7.58,0,11.7,4.68,11.7,10.47a9.28,9.28,0,0,1-7.14,9.43c4.43.68,7.95,5.05,7.95,10,0,6.41-4.19,11.15-12,11.15Zm24.51-29.45c0-3-2.09-5.29-5.73-5.29H335.75V212.7h11.58C351,212.7,353.06,210.55,353.06,207.4Zm.8,17.38c0-3.08-2.15-5.73-6.22-5.73H335.75V230.5h11.89C351.52,230.5,353.86,228.35,353.86,224.78Z"
          transform="translate(-45.96 -45.88)"
        />
        <path
          className="cls-1"
          d="M366.74,216.34c0-12.26,8.68-21.26,21-21.26s21,9,21,21.26-8.75,21.25-21,21.25S366.74,228.59,366.74,216.34Zm34.62,0c0-8.51-5.36-14.85-13.61-14.85s-13.62,6.34-13.62,14.85,5.3,14.84,13.62,14.84S401.36,224.78,401.36,216.34Z"
          transform="translate(-45.96 -45.88)"
        />
        <path
          className="cls-1"
          d="M426.31,236.85l-16.14-41.09h8.19L430.81,229l12.44-33.2h8.19L435.3,236.85Z"
          transform="translate(-45.96 -45.88)"
        />
        <path
          className="cls-1"
          d="M455.88,236.85V195.76H484v6.35H463.09V212.7H483.6v6.35H463.09V230.5H484v6.35Z"
          transform="translate(-45.96 -45.88)"
        />
        <path
          className="cls-1"
          d="M514.89,236.85l-9.05-15.34h-7.21v15.34h-7.2V195.76h18c8.14,0,13.49,5.24,13.49,12.88,0,7.27-4.8,11.27-9.79,12.07l10.1,16.14Zm.68-28.28c0-3.94-3-6.46-7-6.46h-9.92v13.06h9.92C512.62,215.17,515.57,212.58,515.57,208.57Z"
          transform="translate(-45.96 -45.88)"
        />
        <path
          className="cls-1"
          d="M527.52,231.06l4.07-5.61a17.65,17.65,0,0,0,13,5.73c5.91,0,8.19-2.89,8.19-5.67,0-8.62-23.9-3.26-23.9-18.35,0-6.84,5.91-12.08,15-12.08,6.34,0,11.58,2.1,15.34,5.79l-4.07,5.36a16.76,16.76,0,0,0-12-4.74c-4.19,0-6.9,2.09-6.9,5.11,0,7.7,23.9,3,23.9,18.24,0,6.83-4.86,12.75-15.89,12.75C536.7,237.59,531.22,234.88,527.52,231.06Z"
          transform="translate(-45.96 -45.88)"
        />
        <path
          className="cls-1"
          d="M566.77,236.85V195.76h28.15v6.35H574V212.7h20.52v6.35H574V230.5h20.95v6.35Z"
          transform="translate(-45.96 -45.88)"
        />
      </g>
    </svg>
  );
}

export function ReplyLogo({ large = false }) {
  return (
    <Link href="/">
      <SLogoContainer $large={large}>
        <RoboticsPlatformReplySmallIcon />
        <RoboticsPlatformReplyIcon />
      </SLogoContainer>
    </Link>
  );
}

const Sidebar = memo<{ isRobotPage?: boolean }>(({ isRobotPage }) => {
  const pathname = usePathname();
  const [isClosed, setIsClosed] = useState(
    localStorage?.getItem(LOCAL_STORAGE.SIDEBAR_CLOSED) === 'true',
  );

  useEffect(() => {
    const userChoice = localStorage?.getItem(LOCAL_STORAGE.SIDEBAR_CLOSED) === 'true';
    if (userChoice) {
      setIsClosed(userChoice);
    }
  }, []);

  const toggle = () => {
    setIsClosed((prevState) => {
      const nextState = !prevState;
      localStorage.setItem(LOCAL_STORAGE.SIDEBAR_CLOSED, nextState.toString());
      return !prevState;
    });
  };

  // const isData
  const isDataPage = pathname.startsWith('/data');

  return (
    <SSidebar isClosed={isClosed} className="border-r border-border">
      <SNavigationList>
        <SidebarLink path="/home" title="Home" icon="home" />
        <SidebarLink path="/sites-manager" title="Sites Manager" icon="site" />
        <SidebarLink path="/acquired-data" title="Acquired Data" icon="database" />
        <SidebarLink path="/data/ai" title="AI Analyse" icon={<ImagesIcon />} />
        {isDataPage && (
          <>
            <SidebarLink path="/data/tags" title="Metadata" icon={<TagsIcon />} sub />
            <SidebarLink path="/data/ai/models" title="AI Models" icon={<BrainIcon />} sub />
            <SidebarLink path="/data/batches" title="Batch Jobs" icon={<LayersIcon />} sub />
          </>
        )}
        <SidebarLink path="/ai-copilot" title="AI Copilot" icon="ai_copilot" />
        {isRobotPage && <SidebarRobot />}
      </SNavigationList>
      <SToggleItem>
        <SNavigationLink
          // @ts-expect-error - empty string would be loop
          href={null}
          as="button"
          onClick={toggle}
          className="flex justify-end space-x-2"
        >
          {!isClosed && <STypography variant="subtitle2">Hide</STypography>}
          <ChevronsLeft className="" />
        </SNavigationLink>
      </SToggleItem>
    </SSidebar>
  );
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;
